import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isRegionCenter } from "@/components/directory/GeoRadiusFilter";

/**
 * Local Mode — a SEPARATE platform-level geographic browsing overlay.
 * Holds {enabled, center, radius}; exposes `localParams` (the extra local_* query
 * params to merge into supported-page API calls) and `localKey` (a stable string
 * for resetKey/effect deps). It never touches any page's own filter state.
 *
 * Persistence: account pref (source of truth when signed in) + localStorage mirror
 * for anonymous browsing and instant hydration. Gated by the server feature flag
 * via GET /api/local-mode/config (admin-only in phase 1).
 */
const LS_KEY = "so_local_mode";
const DEFAULT = { enabled: false, center: null, radius: 50 };

const LocalModeContext = createContext(null);

function loadLS() {
  try {
    const v = JSON.parse(localStorage.getItem(LS_KEY));
    return v && typeof v === "object" ? { ...DEFAULT, ...v } : { ...DEFAULT };
  } catch {
    return { ...DEFAULT };
  }
}

function saveLS(s) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify({ enabled: s.enabled, center: s.center, radius: s.radius }));
  } catch { /* ignore */ }
}

export function LocalModeProvider({ children }) {
  const { user } = useAuth();
  const [config, setConfig] = useState({ enabled: false, visible: false });
  const [state, setState] = useState(loadLS);
  const hydratedFor = useRef(null);

  useEffect(() => {
    let ok = true;
    api.get("/local-mode/config")
      .then(({ data }) => { if (ok) setConfig(data || { enabled: false, visible: false }); })
      .catch(() => { if (ok) setConfig({ enabled: false, visible: false }); });
    return () => { ok = false; };
  }, [user]);

  // Hydrate from the account pref (source of truth) once per signed-in user.
  useEffect(() => {
    if (!config.visible || !user) return;
    if (hydratedFor.current === user.user_id) return;
    hydratedFor.current = user.user_id;
    api.get("/prefs/local-mode").then(({ data }) => {
      if (!data) return;
      const next = { enabled: !!data.enabled, center: data.location || null, radius: data.radius || 50 };
      setState(next);
      saveLS(next);
    }).catch(() => { /* keep localStorage state */ });
  }, [config.visible, user]);

  const persist = useCallback((next) => {
    saveLS(next);
    if (user && config.visible) {
      api.put("/prefs/local-mode", {
        enabled: next.enabled,
        location: next.center ? {
          label: next.center.label, lat: next.center.lat, lng: next.center.lng,
          granularity: next.center.granularity,
          state_code: next.center.state_code, country_code: next.center.country_code,
        } : null,
        radius: next.radius,
      }).catch(() => { /* localStorage still holds it */ });
    }
  }, [user, config.visible]);

  const update = useCallback((patch) => {
    setState((prev) => { const next = { ...prev, ...patch }; persist(next); return next; });
  }, [persist]);

  const setEnabled = useCallback((v) => update({ enabled: !!v }), [update]);
  const setRadius = useCallback((r) => update({ radius: r }), [update]);
  // Maps a GeoRadiusFilter onChange patch onto Local Mode state.
  const applyGeoPatch = useCallback((patch) => {
    const next = {};
    if ("center" in patch) next.center = patch.center;
    if ("radius" in patch) next.radius = patch.radius;
    if (patch.geoOn === false) next.center = null;
    update(next);
  }, [update]);

  const active = !!(config.visible && state.enabled && state.center);

  const localParams = (() => {
    if (!active) return null;
    const c = state.center;
    if (isRegionCenter(c)) {
      return c.granularity === "state" ? { local_state: c.state_code } : { local_country: c.country_code };
    }
    return { local_lat: c.lat, local_lng: c.lng, local_radius: state.radius };
  })();

  // Bumped by the REFRESH button; folded into localKey so every supported page
  // (which already keys its fetch effect on localKey) re-fetches in place.
  const [refreshNonce, setRefreshNonce] = useState(0);
  const refresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  const baseKey = localParams ? JSON.stringify(localParams) : "off";
  const localKey = refreshNonce ? `${baseKey}#${refreshNonce}` : baseKey;

  const value = {
    visible: config.visible, config,
    enabled: state.enabled, center: state.center, radius: state.radius,
    active, localParams, localKey,
    setEnabled, setRadius, applyGeoPatch, refresh,
  };
  return <LocalModeContext.Provider value={value}>{children}</LocalModeContext.Provider>;
}

export const useLocalMode = () =>
  useContext(LocalModeContext) || {
    visible: false, config: { enabled: false, visible: false },
    enabled: false, center: null, radius: 50,
    active: false, localParams: null, localKey: "off",
    setEnabled: () => {}, setRadius: () => {}, applyGeoPatch: () => {}, refresh: () => {},
  };
